import datapoints from './datapoints';
import setRowsFlagStateHelper from './helpers/setRowsFlagStateHelper';
import { userMailInteractionAction } from 'owa-mail-actions';
import applyConversationFlagInListView from 'owa-mail-list-actions/lib/actions/triage/applyConversationFlag';
import { getApplyConversationActionContext, getRowIdsFromRowKeys } from 'owa-mail-list-store';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import { InstrumentationContext } from 'owa-mail-store';
import { flagConversationService } from 'owa-mail-store/lib/services/flagConversationService';
import FlagType from 'owa-service/lib/contract/FlagType';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import {
    ApplyConversationFlagStateStoreUpdate,
    ApplyConversationFlagStateStoreUpdateActionName,
} from 'owa-mail-store-actions';

const SetConversationsFlagStateActionName = 'setConversationsFlagState';

/**
 * Set the specified flag state on a collection of conversations.
 * @param conversationRowKeys - the converations row keys
 * @param tableViewId - the table view the conversations are in
 * @param flagTypeToApply - the specified flag type to set
 */
const setConversationsFlagStateInternal = createLegacyActionWithDatapoint(
    SetConversationsFlagStateActionName,
    datapoints.setConversationsFlagState
)(function setConversationsFlagStateInternal(
    conversationRowKeys: string[],
    instrumentationContexts: InstrumentationContext[],
    tableViewId: string,
    flagTypeToApply: FlagType,
    actionSource: ActionSource
) {
    const conversationIdStrings = getRowIdsFromRowKeys(conversationRowKeys, tableViewId);
    const conversationActionContexts = conversationRowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableViewId)
    );

    // update list view store
    applyConversationFlagInListView(conversationRowKeys, flagTypeToApply, tableViewId);

    // update reading pane store
    raiseAction<ApplyConversationFlagStateStoreUpdate>(
        ApplyConversationFlagStateStoreUpdateActionName
    )(conversationIdStrings, flagTypeToApply, tableViewId);

    const tableView = listViewStore.tableViews.get(tableViewId);

    // Do not specify folderId in search, so that action is global to the same conversation in all folders.
    const sourceFolderId = tableView.serverFolderId;
    let instrumentationFlag;
    switch (flagTypeToApply.FlagStatus) {
        case 'Flagged':
            instrumentationFlag = 'Flag';
            break;
        case 'NotFlagged':
            instrumentationFlag = 'FlagCleared';
            break;
        case 'Complete':
            instrumentationFlag = 'FlagComplete';
            break;
    }
    userMailInteractionAction(instrumentationFlag, instrumentationContexts);
    flagConversationService(conversationActionContexts, flagTypeToApply, sourceFolderId);
});

/**
 * Set the specified flag state on a collection of conversations.
 * @param conversationRowKeys - the converations row keys
 * @param tableViewId - the table view the conversations are in
 * @param flagType - the specified flag type to set
 */
export default function setConversationsFlagState(
    conversationRowKeys: string[],
    instrumentationContexts: InstrumentationContext[],
    tableViewId: string,
    flagTypeToApply: FlagType,
    actionSource: ActionSource,
    override?: boolean
) {
    setRowsFlagStateHelper(
        conversationRowKeys,
        tableViewId,
        flagTypeToApply,
        (rowKeys: string[], flagTypeToApply: FlagType) => {
            setConversationsFlagStateInternal(
                rowKeys,
                instrumentationContexts,
                tableViewId,
                flagTypeToApply,
                actionSource
            );
        },
        override
    );
}
